// src/pages/products/EditProductPage/components/PricingTab.jsx
import React, { useState } from 'react';
import { DollarSign, Hash, Weight, Building2, Loader2, Truck, Tag } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import OffersModal from '../../components/OffersModal';
import styles from '../styles/PricingTab.module.css';

const PricingTab = ({
  formData,
  shops,
  errors,
  skuValidation,
  handleInputChange,
  handleShopDataChange,
  isEdit = false
}) => {
  const { t } = useLocalization();
  const [offersModalIndex, setOffersModalIndex] = useState(null);

  if (formData.productType === 'variable') {
    return (
      <div className={styles.formCard}>
        <div className={styles.cardHeader}>
          <h2>{t('products.basicPricingInfo')}</h2>
          <p>{isEdit ? t('products.basicPricingInfoEditDesc') : t('products.basicPricingInfoDesc')}</p>
        </div>
        <div className={styles.cardContent}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="sku">{t('products.sku')}</label>
              <div className={styles.inputWithIcon}>
                <Hash size={18} className={styles.inputIcon} />
                <input
                  type="text"
                  id="sku"
                  name="sku"
                  value={formData.sku}
                  onChange={handleInputChange}
                  placeholder={t('products.skuPlaceholder')}
                  className={errors.sku ? 'error' : ''}
                />
                {skuValidation?.mainSku?.isValidating && (
                  <Loader2 size={16} className={`${styles.inputIcon} ${styles.validationSpinner}`} />
                )}
              </div>
              {errors.sku && <span className={styles.errorText}>{errors.sku}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="barcode">{t('products.barcode')}</label>
              <div className={styles.inputWithIcon}>
                <Hash size={18} className={styles.inputIcon} />
                <input
                  type="text"
                  id="barcode"
                  name="barcode"
                  value={formData.barcode}
                  onChange={handleInputChange}
                  placeholder={t('products.barcodePlaceholder')}
                />
              </div>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="buyingPrice">{t('products.buyingPrice')}</label>
              <div className={styles.inputWithIcon}>
                <DollarSign size={18} className={styles.inputIcon} />
                <input
                  type="number"
                  id="buyingPrice"
                  name="buyingPrice"
                  value={formData.buyingPrice}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="weight">{t('products.weight')} (kg)</label>
              <div className={styles.inputWithIcon}>
                <Weight size={18} className={styles.inputIcon} />
                <input
                  type="number"
                  id="weight"
                  name="weight"
                  value={formData.weight}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="length">{t('products.length')} (cm)</label>
              <div className={styles.inputWithIcon}>
                <Hash size={18} className={styles.inputIcon} />
                <input
                  type="number"
                  id="length"
                  name="length"
                  value={formData.dimensions?.length || ''}
                  onChange={(e) => handleInputChange({
                    target: { name: 'dimensions', value: { ...formData.dimensions, length: e.target.value } }
                  })}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="width">{t('products.width')} (cm)</label>
              <div className={styles.inputWithIcon}>
                <Hash size={18} className={styles.inputIcon} />
                <input
                  type="number"
                  id="width"
                  name="width"
                  value={formData.dimensions?.width || ''}
                  onChange={(e) => handleInputChange({
                    target: { name: 'dimensions', value: { ...formData.dimensions, width: e.target.value } }
                  })}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="height">{t('products.height')} (cm)</label>
              <div className={styles.inputWithIcon}>
                <Hash size={18} className={styles.inputIcon} />
                <input
                  type="number"
                  id="height"
                  name="height"
                  value={formData.dimensions?.height || ''}
                  onChange={(e) => handleInputChange({
                    target: { name: 'dimensions', value: { ...formData.dimensions, height: e.target.value } }
                  })}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.formCard}>
      <div className={styles.cardHeader}>
        <h2>{t('products.pricingInfo')}</h2>
        <p>{isEdit ? t('products.pricingInfoEditDesc') : t('products.pricingInfoDesc')}</p>
      </div>
      <div className={styles.cardContent}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="sku">{t('products.sku')}</label>
            <div className={styles.inputWithIcon}>
              <Hash size={18} className={styles.inputIcon} />
              <input
                type="text"
                id="sku"
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                placeholder={t('products.skuPlaceholder')}
                className={errors.sku ? 'error' : ''}
              />
              {skuValidation?.mainSku?.isValidating && (
                <Loader2 size={16} className={`${styles.inputIcon} ${styles.validationSpinner}`} />
              )}
            </div>
            {errors.sku && <span className={styles.errorText}>{errors.sku}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="barcode">{t('products.barcode')}</label>
            <div className={styles.inputWithIcon}>
              <Hash size={18} className={styles.inputIcon} />
              <input
                type="text"
                id="barcode"
                name="barcode"
                value={formData.barcode}
                onChange={handleInputChange}
                placeholder={t('products.barcodePlaceholder')}
              />
            </div>
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="buyingPrice">{t('products.buyingPrice')}</label>
            <div className={styles.inputWithIcon}>
              <DollarSign size={18} className={styles.inputIcon} />
              <input
                type="number"
                id="buyingPrice"
                name="buyingPrice"
                value={formData.buyingPrice}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="weight">{t('products.weight')} (kg)</label>
            <div className={styles.inputWithIcon}>
              <Weight size={18} className={styles.inputIcon} />
              <input
                type="number"
                id="weight"
                name="weight"
                value={formData.weight}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="length">{t('products.length')} (cm)</label>
            <div className={styles.inputWithIcon}>
              <Hash size={18} className={styles.inputIcon} />
              <input
                type="number"
                id="length"
                name="length"
                value={formData.dimensions?.length || ''}
                onChange={(e) => handleInputChange({
                  target: { name: 'dimensions', value: { ...formData.dimensions, length: e.target.value } }
                })}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="width">{t('products.width')} (cm)</label>
            <div className={styles.inputWithIcon}>
              <Hash size={18} className={styles.inputIcon} />
              <input
                type="number"
                id="width"
                name="width"
                value={formData.dimensions?.width || ''}
                onChange={(e) => handleInputChange({
                  target: { name: 'dimensions', value: { ...formData.dimensions, width: e.target.value } }
                })}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="height">{t('products.height')} (cm)</label>
            <div className={styles.inputWithIcon}>
              <Hash size={18} className={styles.inputIcon} />
              <input
                type="number"
                id="height"
                name="height"
                value={formData.dimensions?.height || ''}
                onChange={(e) => handleInputChange({
                  target: { name: 'dimensions', value: { ...formData.dimensions, height: e.target.value } }
                })}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Shop-specific pricing */}
        <div className={styles.shopDataSection}>
          <h3>{t('products.shopPricing')}</h3>
          <p className={styles.sectionDesc}>{t('products.shopPricingDesc')}</p>

          <div className={styles.shopDataGrid}>
            {shops.map((shop, index) => (
              <div
                key={shop.id}
                className={`${styles.shopCard} ${formData.shopData[index]?.enabled ? styles.shopCardEnabled : ''}`}
              >
                <div className={styles.shopHeader}>
                  <label className={styles.shopCheckbox}>
                    <input
                      type="checkbox"
                      checked={formData.shopData[index]?.enabled || false}
                      onChange={(e) => handleShopDataChange(index, 'enabled', e.target.checked)}
                    />
                    <Building2 size={16} className={styles.shopIcon} />
                    <span className={styles.shopName}>{shop.name}</span>
                  </label>
                  <span className={styles.shopType}>{shop.type}</span>
                </div>

                {formData.shopData[index]?.enabled && (
                  <div className={styles.shopFields}>
                    <div className={styles.formGroup}>
                      <label>{t('products.salesPrice')} *</label>
                      <div className={styles.inputWithIcon}>
                        <DollarSign size={16} className={styles.inputIcon} />
                        <input
                          type="number"
                          value={formData.shopData[index]?.salesPrice ?? ''}
                          onChange={(e) => handleShopDataChange(index, 'salesPrice', e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          className={errors.shopData?.[index]?.salesPrice ? 'error' : ''}
                          required
                        />
                      </div>
                      {errors.shopData?.[index]?.salesPrice && (
                        <span className={styles.errorText}>{errors.shopData[index].salesPrice}</span>
                      )}
                    </div>

                    {shop.type === 'dropshipper' && (
                      <div className={styles.formGroup}>
                        <label>{t('products.dropshippingPrice')} *</label>
                        <div className={styles.inputWithIcon}>
                          <DollarSign size={16} className={styles.inputIcon} />
                          <input
                            type="number"
                            value={formData.shopData[index]?.dropshippingPrice || ''}
                            onChange={(e) => handleShopDataChange(index, 'dropshippingPrice', e.target.value)}
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            className={errors.shopData?.[index]?.dropshippingPrice ? 'error' : ''}
                            required
                          />
                        </div>
                        {errors.shopData?.[index]?.dropshippingPrice && (
                          <span className={styles.errorText}>{errors.shopData[index].dropshippingPrice}</span>
                        )}
                      </div>
                    )}

                    {/* Override Delivery Charge */}
                    <div className={styles.overrideDeliverySection}>
                      <label className={styles.overrideDeliveryCheckbox}>
                        <input
                          type="checkbox"
                          checked={formData.shopData[index]?.overrideDeliveryChargeEnabled || false}
                          onChange={(e) => {
                            handleShopDataChange(index, 'overrideDeliveryChargeEnabled', e.target.checked);
                            if (!e.target.checked) {
                              handleShopDataChange(index, 'overrideDeliveryCharge', null);
                            }
                          }}
                        />
                        <Truck size={14} className={styles.overrideDeliveryIcon} />
                        <span>Override Delivery Charge</span>
                      </label>

                      {formData.shopData[index]?.overrideDeliveryChargeEnabled && (
                        <div className={styles.formGroup}>
                          <div className={styles.inputWithIcon}>
                            <DollarSign size={16} className={styles.inputIcon} />
                            <input
                              type="number"
                              value={formData.shopData[index]?.overrideDeliveryCharge ?? ''}
                              onChange={(e) =>
                                handleShopDataChange(index, 'overrideDeliveryCharge', e.target.value)
                              }
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Quantity-Based Offers */}
                    <button
                      type="button"
                      className={`${styles.offersButton} ${formData.shopData[index]?.offers?.length > 0 ? styles.offersButtonActive : ''}`}
                      onClick={() => setOffersModalIndex(index)}
                    >
                      <Tag size={14} />
                      Offers
                      {formData.shopData[index]?.offers?.length > 0 && (
                        <span className={styles.offersBadge}>{formData.shopData[index].offers.length}</span>
                      )}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {offersModalIndex !== null && (
            <OffersModal
              isOpen
              onClose={() => setOffersModalIndex(null)}
              onSave={(newOffers) => {
                handleShopDataChange(offersModalIndex, 'offers', newOffers);
                setOffersModalIndex(null);
              }}
              offers={formData.shopData[offersModalIndex]?.offers || []}
              productName={shops[offersModalIndex]?.name || ''}
            />
          )}

          {isEdit && (
            <div className={styles.editModeNotice}>
              <p>{t('products.editModeStockNotice') || 'Stock quantities are managed separately and not updated through product editing.'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingTab;